import React, { useCallback } from "react";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import { format, addMinutes, isPast, subMinutes } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  Alert,
  AlertTitle,
  DialogContent,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  X,
  Clock,
  User,
  IdentificationBadge,
  Repeat,
  DotsThreeVertical,
  PencilSimpleLine,
  Copy,
  ArrowRight,
  CalendarX,
  ArrowSquareOut,
  UsersThree,
  SealCheck,
  Target,
} from "@phosphor-icons/react";
import { useTheme } from "@mui/styles";
import Button from "@mui/material/Button";
import MarkAsNoShowDialog from "./MarkAsNoShowDialog";
import { useGetFeatureFlag } from "hooks/useGetFeatureFlag";
import { formatRepeatDays } from "components/EventScheduling/EventSchedulingUtils";
import { LoadingButton } from "@mui/lab";
import { useNonVideoEventMutation } from "components/EventScheduling/useNonVideoEventMutation";
import useRescheduleVideoCall from "hooks/api/appointments/useRescheduleVideoCall";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useJoinAppointment, isJoinable } from "hooks/useJoinAppointment";

const SessionDetails = ({
  userPermissions,
  detailsOpen,
  sessionDetails,
  onCloseDetailsDialog,
  copySessionURL,
  onClickEditSession,
  userTimezone,
  userId,
  onMarkClientAsNoShow,
  history,
  isSupervisor,
  isDirector,
  fetchClinicianWeeklySessions,
}) => {
  const {
    id,
    client_name,
    title,
    scheduled_date,
    call_duration,
    is_recurring,
    video_key,
    end_date,
    repeatType,
    clinician_name,
    secondary_clinician_name,
    didNotOccur,
    off_platform,
    primary_clinician_user_id,
    client_id,
    clinician_id,
    secondary_clinician_id,
    end,
    note_completed,
    no_show,
    canceled,
    secondary_clinician_user_id,
    start_date,
    is_video_call,
    team_member_name,
    user_id,
    is_tentative,
  } = sessionDetails;

  const theme = useTheme();
  const { rescheduleNonVideoEvent } = useNonVideoEventMutation();
  const { showToast } = useGlobalToast();
  const { isRescheduleVideoCallLoading, rescheduleVideoCall } = useRescheduleVideoCall();
  const { joinAppointment } = useJoinAppointment(sessionDetails);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showMarkAsNoShowDialog, setShowMarkAsNoShowDialog] = React.useState(false);
  const optionsOpen = Boolean(anchorEl);
  const handleOpenOptions = (event) => setAnchorEl(event.currentTarget);
  const isExpired = new Date(end) < new Date();
  const showNotesAlert = end_date && !didNotOccur && !note_completed && is_video_call;
  const isDoxyCall = !(video_key && !off_platform) && is_video_call;
  const isUserPrimaryClinician = userId === primary_clinician_user_id;
  const isUserSecondaryClinician = userId === secondary_clinician_user_id;
  const canMarkAsNoShow =
    !didNotOccur &&
    !start_date &&
    isUserPrimaryClinician &&
    new Date() > addMinutes(new Date(scheduled_date), 10);

  const canSeeJoinEventButton =
    !isExpired &&
    !end_date &&
    (isUserPrimaryClinician || isUserSecondaryClinician) &&
    !canceled &&
    is_video_call &&
    !is_tentative &&
    // In-person assessments have no video session to join — hide the button.
    isJoinable(sessionDetails);

  const showTentativeApproval = is_tentative && !isPast(new Date(scheduled_date));
  const { data: schedulePageFeatureFlag } = useGetFeatureFlag(userId, "scheduling_page");
  const getDialogWidth = useCallback(() => {
    if (showNotesAlert || no_show || canceled) return 512;
    else if (showTentativeApproval) return 600;
    return 334;
  }, [showNotesAlert, no_show, canceled, is_tentative]);

  const approveTentativeEvent = () => {
    const onScheduleSuccess = () => {
      fetchClinicianWeeklySessions();
      onCloseDetailsDialog();
      showToast({ message: "Appointment approved!" });
    };

    const sharedPayload = {
      startDate: sessionDetails.scheduled_date,
      endDate: addMinutes(new Date(sessionDetails.scheduled_date), sessionDetails.call_duration),
      clinicianUserId: sessionDetails.primary_clinician_user_id || sessionDetails.clinician_user_id,
      secondaryClinicianUserId: sessionDetails.secondary_clinician_user_id,
      repeatDays: formatRepeatDays(sessionDetails.repeat_days),
      overrideSchedule: true,
      repeatEndType: sessionDetails.repeat_type,
      repeatUntilDate: sessionDetails.repeat_until_date,
      timeUnit: sessionDetails.time_unit,
      timeInterval: sessionDetails.time_interval,
      isRepeating: sessionDetails.is_recurring,
      memberId: sessionDetails.user_id,
      isTentative: false,
      rescheduleAllInstances: sessionDetails.is_recurring,
      timezone: sessionDetails.call_timezone,
    };
    if (sessionDetails.is_video_call) {
      rescheduleVideoCall(
        {
          ...sharedPayload,
          videoId: sessionDetails.id,
          offPlatform: sessionDetails.off_platform,
          billingType: sessionDetails.billing_type,
        },
        { onSuccess: onScheduleSuccess }
      );
    } else {
      rescheduleNonVideoEvent.mutate(
        {
          ...sharedPayload,
          id: sessionDetails.id,
          override: true,
          serviceType: sessionDetails.service_type,
        },
        {
          onSuccess: onScheduleSuccess,
          onError: () => {
            showToast({
              message: "Error updating appointment",
              errorState: true,
            });
          },
        }
      );
    }
  };

  if (!detailsOpen || !scheduled_date) return null;

  return (
    <Dialog
      open={detailsOpen}
      onClose={onCloseDetailsDialog}
      PaperProps={{ sx: { width: getDialogWidth() } }}
    >
      <DialogContent sx={{ padding: "20px 24px" }}>
        <Stack gap={theme.spacing(5)}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h3">{title}</Typography>
            <Stack direction="row" alignItems="center">
              <IconButton
                size="small"
                fontSize="small"
                aria-label="options"
                onClick={handleOpenOptions}
                id="open-menu-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <DotsThreeVertical />
              </IconButton>
              <IconButton
                size="small"
                fontSize="small"
                aria-label="close"
                onClick={onCloseDetailsDialog}
              >
                <X />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" gap={theme.spacing(3)}>
            <Tooltip title="Event time" placement="top">
              <Clock fontSize={20} weight="duotone" />
            </Tooltip>
            <Typography variant="body2">
              {format(new Date(scheduled_date), "h:mm a")}
              {" - "}
              {formatInTimeZone(
                addMinutes(new Date(scheduled_date), call_duration),
                userTimezone,
                "h:mm a (zzz)"
              )}
            </Typography>
          </Stack>
          <Stack direction="row" gap={theme.spacing(3)}>
            <Tooltip title="Client" placement="top">
              <User fontSize={20} weight="duotone" />
            </Tooltip>
            <Link
              href={`/clients/${client_id}/dashboard`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {client_name}
            </Link>
          </Stack>
          {(userPermissions?.view_clinician_list ||
            isUserPrimaryClinician ||
            isUserSecondaryClinician) && (
            <Stack direction="row" gap={theme.spacing(3)}>
              <Tooltip title="Clinician" placement="top">
                <IdentificationBadge fontSize={20} weight="duotone" />
              </Tooltip>
              <Stack direction="row" flexWrap="wrap">
                <Link
                  href={`/clinicians/${clinician_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {clinician_name}
                  {secondary_clinician_id && ","}
                </Link>
                {secondary_clinician_id && (
                  <>
                    {/* Space (\u00A0) is added here to avoid rendering the underline under it */}
                    {"\u00A0"}
                    <Link
                      href={`/clinicians/${secondary_clinician_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {secondary_clinician_name}
                    </Link>
                  </>
                )}
              </Stack>
            </Stack>
          )}
          {team_member_name && (
            <Stack direction="row" gap={theme.spacing(3)}>
              <Tooltip title="Team Member" placement="top">
                <UsersThree fontSize={20} weight="duotone" />
              </Tooltip>
              <Link
                href={`/clients/${client_id}/profile?teamMember=${user_id}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {team_member_name}
              </Link>
            </Stack>
          )}
          {is_recurring && (
            <Stack direction="row" gap={theme.spacing(3)}>
              <Tooltip title="Repeating event details" placement="top">
                <Repeat fontSize={20} />
              </Tooltip>
              <Typography variant="body2">{repeatType}</Typography>
            </Stack>
          )}
          {isDoxyCall && (
            <Stack direction="row" gap={theme.spacing(3)}>
              <Tooltip title="Off platform session" placement="top">
                <ArrowSquareOut fontSize={20} />
              </Tooltip>
              <Typography variant="body2">Off platform session</Typography>
            </Stack>
          )}
          <Stack gap={theme.spacing(3)}>
            {process.env.CENTRAL_REACH_URL && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Target />}
                component="a"
                href={`${process.env.CENTRAL_REACH_URL.replace(/\/$/, "")}/data-sheets/${encodeURIComponent(String(client_id))}${id != null ? `?appointmentId=${encodeURIComponent(String(id))}&date=${encodeURIComponent(formatInTimeZone(new Date(start_date ?? scheduled_date), userTimezone, "yyyy-MM-dd"))}` : ""}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Data Sheet
              </Button>
            )}
            {canSeeJoinEventButton && (
              <Button
                onClick={() => joinAppointment({ offPlatform: off_platform, videoKey: video_key })}
                endIcon={isDoxyCall ? <ArrowSquareOut /> : null}
                disabled={new Date() < subMinutes(new Date(scheduled_date), 60)}
              >
                Join Event{isDoxyCall && " with Doxy"}
              </Button>
            )}
          </Stack>
          {showNotesAlert && (
            <Alert
              severity="info"
              action={
                <Button
                  endIcon={<ArrowRight />}
                  size="small"
                  variant="text"
                  color="info"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={() => history.push("/billing")}
                >
                  Go to Billing
                </Button>
              }
            >
              Therapy notes will be available after submission
            </Alert>
          )}
          {no_show && <Alert severity="error">The event was marked as a no-show</Alert>}
          {canceled && (
            <Alert severity="error">
              <AlertTitle>Event was canceled</AlertTitle>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      {showTentativeApproval && (
        <Alert
          severity="warning"
          action={
            <LoadingButton
              loading={isRescheduleVideoCallLoading || rescheduleNonVideoEvent.isLoading}
              onClick={approveTentativeEvent}
              sx={{ width: "max-content" }}
              size="small"
              startIcon={<SealCheck weight="duotone" />}
            >
              Approve Event
            </LoadingButton>
          }
        >
          <AlertTitle>Tentative Event</AlertTitle>
          Approve this appointment to confirm and send participants notifications for this and any
          future events
        </Alert>
      )}
      <Menu
        anchorEl={anchorEl}
        open={optionsOpen}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-labelledby": "open-menu-button" }}
      >
        <MenuItem
          onClick={() => {
            if (schedulePageFeatureFlag)
              history.push(
                `/clinician-schedule/create-event?id=${
                  sessionDetails.id
                }&isNonVideoEvent=${!sessionDetails.is_video_call}`
              );
            else onClickEditSession(is_recurring);
            setAnchorEl(null);
          }}
          disabled={isExpired || (isUserSecondaryClinician && !isSupervisor && !isDirector)}
        >
          <ListItemIcon>
            <PencilSimpleLine />
          </ListItemIcon>
          Edit Event
        </MenuItem>
        <MenuItem
          onClick={() => {
            copySessionURL(video_key, off_platform);
            setAnchorEl(null);
          }}
          disabled={isExpired || end_date || !is_video_call || !isJoinable(sessionDetails)}
        >
          <ListItemIcon>
            <Copy />
          </ListItemIcon>
          Copy Event URL
        </MenuItem>
        <MenuItem
          onClick={() => {
            setShowMarkAsNoShowDialog(true);
            setAnchorEl(null);
          }}
          disabled={!canMarkAsNoShow}
        >
          <ListItemIcon>
            <CalendarX />
          </ListItemIcon>
          Mark as no-show
        </MenuItem>
      </Menu>
      <MarkAsNoShowDialog
        open={showMarkAsNoShowDialog}
        handleClose={() => setShowMarkAsNoShowDialog(false)}
        onMarkClientAsNoShow={() => {
          onMarkClientAsNoShow(id);
          setShowMarkAsNoShowDialog(false);
        }}
      />
    </Dialog>
  );
};
export default SessionDetails;
